import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { HasRoles } from '@voxityai/auth/components';
import { auth } from 'data/firestore/firebase';
import Button from 'components/Button';
import ModalBody from 'components/ModalBody';
import Trash from 'components/icons/trash';
import IdCard from 'components/icons/idcard';
import AlertTriangle from 'components/icons/alertTriangle';
import {
    useNexhealthPatient,
    useNexhealthPatientLink,
    useUnlinkNexhealthPatient,
} from '../hooks';
import LinkPatientDrawer from '../LinkPatientDrawer';
import Demographics from './demographics';
import Alerts from './alerts';
import Appointments from './appointments';
import {
    Container,
    Content,
    EmptyMessageContainer,
    EmptyMessageDescription,
    EmptyMessageIcon,
    EmptyMessageTitle,
    SpinnerContainer,
    StyledSpinner,
} from './styled';

const CustomerNexhealthTab = () => {
    const { agentId = '', customerId = '' } = useParams();
    const [isLinkDrawerOpen, setLinkDrawerOpen] = useState(false);

    const {
        data: linkResult,
        isLoading: isLoadingLink,
        isError: isLinkError,
        refetch: refetchLink,
    } = useNexhealthPatientLink(agentId, customerId, true);
    const [link] = linkResult?.data || [];
    const isLinked = Boolean(link && !link.stale);

    const {
        data: patientResult,
        isLoading: isLoadingPatient,
        isError: isPatientError,
        refetch: refetchPatient,
    } = useNexhealthPatient(agentId, customerId, isLinked);
    const patient = patientResult?.data;

    const { mutateAsync: unlinkPatient } = useUnlinkNexhealthPatient(agentId, customerId);

    const handleUnlink = useCallback(async () => {
        if (!link) return;
        const result = await confirmModal({
            header: '',
            variant: 'destructive',
            question: (
                <ModalBody
                    variant="destructive"
                    icon={<Trash />}
                    title="Unlink NexHealth patient?"
                    description="This customer will no longer be linked to this NexHealth patient."
                />
            ),
            borderRadius: 'semi-square',
            okButtonLabel: 'Unlink',
            cancelButtonLabel: 'Cancel',
        });
        if (!result) return;

        showAppSpinner();
        try {
            await unlinkPatient({
                pathname: `/organizations/${agentId}/apps/nexhealth/patient-links/${link.id}`,
            });
        } catch (error) {
            showAppNotification({
                title: 'Error',
                description: (error as any)?.message || 'Something went wrong. Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId, link, unlinkPatient]);

    const isLoading = isLoadingLink || (isLinked && isLoadingPatient);
    const isError = isLinkError || (isLinked && isPatientError);

    return (
        <Container>
            <Content>
                <RenderIf isTrue={isLoading}>
                    <SpinnerContainer>
                        <StyledSpinner variant="brand" type="arc" />
                    </SpinnerContainer>
                </RenderIf>
                <RenderIf isTrue={!isLoading && isError}>
                    <EmptyMessageContainer>
                        <EmptyMessageIcon>
                            <AlertTriangle />
                        </EmptyMessageIcon>
                        <EmptyMessageTitle>Couldn&apos;t load NexHealth</EmptyMessageTitle>
                        <EmptyMessageDescription>
                            Something went wrong while loading the NexHealth information
                            for this customer.
                        </EmptyMessageDescription>
                        <Button
                            variant="brand"
                            borderRadius="semi-rounded"
                            label="Try again"
                            onClick={() => (isLinkError ? refetchLink() : refetchPatient())}
                        />
                    </EmptyMessageContainer>
                </RenderIf>
                <RenderIf isTrue={!isLoading && !isError && !link}>
                    <EmptyMessageContainer>
                        <EmptyMessageIcon>
                            <IdCard />
                        </EmptyMessageIcon>
                        <EmptyMessageTitle>Not linked to NexHealth</EmptyMessageTitle>
                        <EmptyMessageDescription>
                            Link this customer to a NexHealth patient to see their demographics,
                            alerts, and appointments here.
                        </EmptyMessageDescription>
                        <HasRoles auth={auth} agentId={agentId} allowedRoles={['owner', 'editor']}>
                            <Button
                                variant="brand"
                                borderRadius="semi-rounded"
                                label="Link patient"
                                onClick={() => setLinkDrawerOpen(true)}
                            />
                        </HasRoles>
                    </EmptyMessageContainer>
                </RenderIf>
                <RenderIf isTrue={!isLoading && !isError && link?.stale}>
                    <EmptyMessageContainer>
                        <EmptyMessageIcon>
                            <AlertTriangle />
                        </EmptyMessageIcon>
                        <EmptyMessageTitle>NexHealth link is out of date</EmptyMessageTitle>
                        <EmptyMessageDescription>
                            The linked patient no longer exists in NexHealth.
                            Unlink it and link this customer to the right patient.
                        </EmptyMessageDescription>
                        <HasRoles auth={auth} agentId={agentId} allowedRoles={['owner', 'editor']}>
                            <Button
                                variant="brand"
                                borderRadius="semi-rounded"
                                label="Unlink"
                                onClick={handleUnlink}
                            />
                        </HasRoles>
                    </EmptyMessageContainer>
                </RenderIf>
                <RenderIf isTrue={!isLoading && !isError && isLinked && patient}>
                    <Demographics
                        patient={patient!}
                        actions={(
                            <HasRoles auth={auth} agentId={agentId} allowedRoles={['owner', 'editor']}>
                                <Button
                                    variant="destructive"
                                    size="small"
                                    borderRadius="semi-rounded"
                                    label="Unlink"
                                    onClick={handleUnlink}
                                />
                            </HasRoles>
                        )}
                    />
                    <Alerts agentId={agentId} patientId={link?.patientId as number} />
                    <Appointments agentId={agentId} customerId={customerId} />
                </RenderIf>
            </Content>
            <LinkPatientDrawer
                agentId={agentId}
                customerId={customerId}
                isOpen={isLinkDrawerOpen}
                onRequestClose={() => setLinkDrawerOpen(false)}
            />
        </Container>
    );
};

export default CustomerNexhealthTab;
